// api
import { ResourceActive } from '~/src/dash/api/console-api';
import {
  parseArray,
  parseNumber,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';

// |------------------ INTERFACES and TYPES ------------------|

export interface IFreewheelSite {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Name: string;
  PublisherID: number;
  FreewheelPublisherID: number;
  Active: ResourceActive;
  FreewheelSiteToSiteAssociations: IFreewheelSiteToSiteAssociation[];
}

export interface IFreewheelSiteToSiteAssociation {
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  SiteID: number;
  FreewheelSiteID: number;
  TargetingDevices: string;
  TargetingDealID: string;
  TargetingAppBundleID: string;
}

// |------------------ TRANSFORM FUNCTIONS ------------------|

export const transformFreewheelSiteToSiteAssociation =
  transformModel<IFreewheelSiteToSiteAssociation>({
    ID: parseNumber(['ID']),
    CreatedAt: 'CreatedAt',
    UpdatedAt: 'UpdatedAt',
    DeletedAt: 'DeletedAt',

    SiteID: parseNumber(['SiteID']),
    FreewheelSiteID: parseNumber(['FreewheelSiteID']),
    TargetingDevices: parseString(['TargetingDevices']),
    TargetingDealID: parseString(['TargetingDealID']),
    TargetingAppBundleID: parseString(['TargetingAppBundleID']),
  });

export const transformFreewheelSite = transformModel<IFreewheelSite>({
  ID: parseNumber(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Name: parseString(['Name']),
  PublisherID: parseNumber(['PublisherID']),
  FreewheelPublisherID: parseNumber(['FreewheelPublisherID']),
  Active: parseNumber(['Active']),
  FreewheelSiteToSiteAssociations: parseArray(
    ['FreewheelSiteToSiteAssociations'],
    transformFreewheelSiteToSiteAssociation,
  ),
});

// |------------------ API CALLS ------------------|

export async function getFreewheelSite(
  networkID: string,
  publisherID: string,
): Promise<IFreewheelSite> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/freewheel_site/${networkID}`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    return transformFreewheelSite(val.parsedBody);
  });
}

export async function getFreewheelSites(publisherID: string): Promise<IFreewheelSite[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/freewheel_sites`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const freewheelSites = [] as IFreewheelSite[];
    if (val.parsedBody) {
      for (const item of val.parsedBody) {
        const freewheelSite = transformFreewheelSite(item);
        freewheelSites.push(freewheelSite);
      }
    }
    return freewheelSites;
  });
}

export async function createFreewheelSite(
  freewheelSite: IFreewheelSite,
  publisherID: string,
): Promise<IFreewheelSite> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/freewheel_sites`);
  const parsedFreewheelSite = transformFreewheelSite(freewheelSite);
  return await restPost<IFreewheelSite>(reqUrl, parsedFreewheelSite).then(
    (val: IHttpResponse<any>) => {
      return transformFreewheelSite(val.parsedBody);
    },
  );
}

// update Freewheel Site will execute a full or partial update of the resource.  A full update will include all fields,
// child associations and will overwrite this data.  A partial update will only update the fields given through
// the Freewheel Site param

// NOTE: Because a full object is required, make sure that all fields in the object are defined in the interface/transform
export async function updateFreewheelSite(
  freewheelSite: IFreewheelSite,
  publisherID: string,
  fullUpdate: boolean,
): Promise<IFreewheelSite> {
  const partialUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/freewheel_site/${freewheelSite.ID}`,
  );
  const reqUrl = fullUpdate ? partialUrl + '&full_update=true' : partialUrl;
  const parsedFreewheelSite = transformFreewheelSite(freewheelSite);
  return await restPatch<IFreewheelSite>(
    reqUrl,
    fullUpdate ? parsedFreewheelSite : freewheelSite,
  ).then((val: IHttpResponse<any>) => {
    return transformFreewheelSite(val.parsedBody);
  });
}

export async function deleteFreewheelSite(
  freewheelSiteID: string,
  publisherID: string,
): Promise<IHttpResponse<IFreewheelSite>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/freewheel_site/${freewheelSiteID}`,
  );
  return await restDelete<IFreewheelSite>(reqUrl, undefined);
}

export async function deleteFreewheelSiteAsso(
  freewheelSiteID: string,
  freewheelSiteAssoID: string,
  publisherID: string,
): Promise<IHttpResponse<IFreewheelSiteToSiteAssociation>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${publisherID}/freewheel_sites/${freewheelSiteID}/freewheel_site_assos/${freewheelSiteAssoID}`,
  );
  return await restDelete<IFreewheelSiteToSiteAssociation>(reqUrl, undefined);
}
